import React, { useState, useEffect } from 'react';
import Button from '../../UI/Button';
import { ColumnMapping, ValidationError, ValidatedRow } from '../ImportWizard';
import { ReferenceData, findIdByName, findOptionValueByName, parseRaceOptionValues } from '../../../services/Referencedataservice';
import { updateSession } from '../../../services/importSessionService';
import { isMatch } from 'date-fns';
import toast from 'react-hot-toast';
import IconButton from "../../UI/IconButton";
import { scrollLeft, scrollRight } from "../../../utils/elementHelpers";
import { optionalDateOfBirthEnabled } from '../../../utils/features';

interface ValidationPreviewStepProps {
    csvData: any[];
    columnMappings: ColumnMapping[];
    referenceData: ReferenceData;
    onNext: (validatedRows: ValidatedRow[]) => void;
    onBack: () => void;
    sessionId: string | null;
}

const handleScrollLeft = () => {
    scrollLeft('.table-container');
};

const handleScrollRight = () => {
    scrollRight('.table-container');
};

const ROWS_PER_PAGE = 50;

const ValidationPreviewStep: React.FC<ValidationPreviewStepProps> = ({
    csvData,
    columnMappings,
    referenceData,
    onNext,
    onBack,
    sessionId,
}) => {
    const [validatedRows, setValidatedRows] = useState<ValidatedRow[]>([]);
    const [showInvalidOnly, setShowInvalidOnly] = useState(false);
    const [editingRow, setEditingRow] = useState<number | null>(null);
    const [editedData, setEditedData] = useState<Record<string, any>>({});
    const [currentPage, setCurrentPage] = useState(1);
    const [validating, setValidating] = useState(true);

    /* useEffect(() => {
        validateData();
    }, [csvData, columnMappings]); */

    function handleStickyScroll() {
        const tableWrapper = document.querySelector('.table-container');
        if (!tableWrapper) return;

        tableWrapper.addEventListener('scroll', function () {
            const isScrolled = tableWrapper.scrollLeft > 0;

            const stickyCells = document.querySelectorAll('.sticky-col.sticky-first, .sticky-col.sticky-second, .sticky-col.sticky-last');

            stickyCells.forEach(cell => {
                if (isScrolled) {
                    cell.classList.add('is-stuck');
                } else {
                    cell.classList.remove('is-stuck');
                }
            });
        });
    }

    function applyStickyColumns() {
        const table = document.querySelector('.validation-table');
        if (!table) return;
        
        const rows = table.querySelectorAll('tr');

        rows.forEach(row => {
            const cells = row.children;
            if (cells.length < 4) return;  // Changed from 3 to 4 since we need 4 columns

            const first = cells[0] as HTMLElement;   // Actions column
            const second = cells[1] as HTMLElement;  // First Name column
            const forth = cells[3] as HTMLElement;   // Last Name column

            const firstWidth = first.offsetWidth - 1;
            const secondWidth = second.offsetWidth - 2;

            // First column (Actions) - sticky at position 0
            first.classList.add('sticky-col');
            first.style.left = '0px';

            // Second column (First Name) - sticky after first column
            second.classList.add('sticky-col');
            second.style.left = firstWidth + 'px';

            // Fourth column (Last Name) - sticky after first AND second columns
            forth.classList.add('sticky-col');
            forth.style.left = (firstWidth + secondWidth) + 'px';
        });
    }

    function removeStickyColumns() {
        const table = document.querySelector('.validation-table');
        if (!table) return;
        
        const rows = table.querySelectorAll('tr');

        rows.forEach(row => {
            const cells = row.children;
            if (cells.length < 4) return;

            const second = cells[1] as HTMLElement;
            const forth = cells[3] as HTMLElement;
            
            second.classList.remove('sticky-col');
            forth.classList.remove('sticky-col');

            return;
        });
    }    

    useEffect(() => {
        validateData();
        
        applyStickyColumns();
        handleStickyScroll();
        window.addEventListener('resize', applyStickyColumns);

        // wait until the table exists in the DOM
        const timer = setTimeout(() => {
            applyStickyColumns();
            handleStickyScroll();
            console.log('applied');
        }, 500);

        return () => clearTimeout(timer);

    }, [csvData, columnMappings]);

    // Default to invalid tab if there are invalid rows
    useEffect(() => {
        if (validatedRows.length > 0 && getInvalidCount() > 0) {
            setShowInvalidOnly(true);
        }
    }, [validatedRows]);


    const validateRow = (row: any, rowIndex: number): ValidatedRow => {
        const errors: ValidationError[] = [];
        const mappedData: Record<string, any> = {};
        
        // Track which household members have any data
        const householdMemberData: Record<number, string[]> = {};
        
        //console.log(columnMappings)
        columnMappings.forEach((mapping) => {
            let value = row[mapping.csvColumn];
            
            // Check if this is a household member field
            const hhMatch = mapping.participantField.match(/^hh(\d+)_(.+)$/);
            if (hhMatch) {
                const hhNumber = parseInt(hhMatch[1]);
                const fieldName = hhMatch[2];
                
                // Track that this household member has some data
                if (value && value.trim() !== '') {
                    if (!householdMemberData[hhNumber]) {
                        householdMemberData[hhNumber] = [];
                    }
                    householdMemberData[hhNumber].push(fieldName);
                }
            }
            
            // Validate required fields for primary participant
            const requiredParticipantFields = optionalDateOfBirthEnabled
                ? ['firstName', 'lastName']
                : ['firstName', 'lastName', 'dateOfBirth'];

            if (requiredParticipantFields.includes(mapping.participantField)) {
                if (!value || value.trim() === '') {
                    errors.push({
                        row: rowIndex,
                        field: mapping.participantField,
                        message: `${formatFieldLabel(mapping.participantField)} is required`,
                        value,
                    });
                }
            }
            // Validate date format (supports MM/dd/yyyy and MM-dd-yyyy)
            if (mapping.participantField.toLowerCase().includes('dateofbirth') && value) {
                const isValidDate = isMatch(value, 'MM/dd/yyyy') || isMatch(value, 'MM-dd-yyyy');
                if (!isValidDate) {
                    errors.push({
                        row: rowIndex,
                        field: mapping.participantField,
                        message: 'Date format should be MM/dd/yyyy or MM-dd-yyyy',
                        value,
                    });
                }
            }

            // Validate date format (supports MM/dd/yyyy and MM-dd-yyyy)
            if (mapping.participantField.toLowerCase().includes('activationdate') && value) {
                const isValidDate = isMatch(value, 'MM/dd/yyyy') || isMatch(value, 'MM-dd-yyyy');
                if (!isValidDate) {
                    errors.push({
                        row: rowIndex,
                        field: mapping.participantField,
                        message: 'Date format should be MM/dd/yyyy or MM-dd-yyyy',
                        value,
                    });
                }
            }

            // Validate email format
            if (mapping.participantField.toLowerCase().includes('email') && value) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) {
                    errors.push({
                        row: rowIndex,
                        field: mapping.participantField,
                        message: 'Email Address is Invalid',
                        value,
                    });
                }
            }

            // Validate phone number
            if (mapping.participantField.toLowerCase().includes('phone') && value) {

                // Remove everything except digits
                let cleanedValue = value.replace(/\D/g, '');

                // Remove US country code if present (11 digits starting with 1)
                if (cleanedValue.length === 11 && cleanedValue.startsWith('1')) {
                    cleanedValue = cleanedValue.substring(1);
                }

                // Validate it must now be exactly 10 digits
                if (cleanedValue.length !== 10) {
                    errors.push({
                        row: rowIndex,
                        field: mapping.participantField,
                        message: 'Phone Number must be ########## or (###)###-####',
                        value,
                    });
                } else {
                    // Format as (314)969-1812
                    value = `(${cleanedValue.substring(0,3)})${cleanedValue.substring(3,6)}-${cleanedValue.substring(6,10)}`;
                }

                /* const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
                if (!phoneRegex.test(value.replace(/\s/g, ''))) {
                    errors.push({
                        row: rowIndex,
                        field: mapping.participantField,
                        message: 'Phone Number is Invalid',
                        value,
                    });
                } */
            }

            // Validate gender
            if (mapping.participantField.toLowerCase().includes('gender') && value) {
                const genderId = findOptionValueByName(value, referenceData.genderOptions);
                if (genderId === undefined) {
                    errors.push({
                        row: rowIndex,
                        field: mapping.participantField,
                        message: `Gender "${value}" not found in system`,
                        value,
                    });
                }
            }

            // Validate ethnicity
            if (mapping.participantField.toLowerCase().includes('ethnicity') && value) {
                const ethnicityId = findOptionValueByName(value, referenceData.ethnicityOptions, { allowSubstring: true });
                //console.log(mapping.participantField, value, referenceData.ethnicities, ethnicityId)
                if (ethnicityId === undefined) {
                    errors.push({
                        row: rowIndex,
                        field: mapping.participantField,
                        message: `Ethnicity "${value}" not found in system`,
                        value,
                    });
                }
            }

            // Validate race
            if (mapping.participantField.toLowerCase().includes('race') && value) {
                const raceSelection = parseRaceOptionValues(value, referenceData);
                if (raceSelection.raceIds.length === 0 && !raceSelection.ethnicityId) {
                    errors.push({
                        row: rowIndex,
                        field: mapping.participantField,
                        message: `Race "${value}" not found in system`,
                        value,
                    });
                }
            }

            // Validate household relationship
            if (mapping.participantField.toLowerCase().includes('relationship') && value !== undefined && value !== null && String(value).trim() !== '') {
                const relationshipId = findIdByName(value, referenceData.relationships);
                if (relationshipId === undefined) {
                    errors.push({
                        row: rowIndex,
                        field: mapping.participantField,
                        message: `Relationship "${value}" not found in system`,
                        value,
                    });
                }
            }

            // Validate program name (supports pipe-delimited multiple programs)
            if (mapping.participantField === 'programName' && value) {
                const programNames = value.split('|').map((p: string) => p.trim()).filter(Boolean);
                const invalidPrograms: string[] = [];

                programNames.forEach((programName: string) => {
                    const programMatch = referenceData.programs.find(
                        p => p.name.toLowerCase() === programName.toLowerCase()
                    );
                    if (!programMatch) {
                        invalidPrograms.push(programName);
                    }
                });
                
                if (invalidPrograms.length > 0) {
                    errors.push({
                        row: rowIndex,
                        field: mapping.participantField,
                        message: `Program(s) "${invalidPrograms.join(', ')}" not found in system`,
                        value,
                    });
                }
            }

            // Validate enrollment date format and count (supports pipe-delimited multiple dates)
            if (mapping.participantField === 'enrollmentDate' && value) {
                const dates = value.split('|').map((d: string) => d.trim()).filter(Boolean);
                let hasInvalidDates = false;

                dates.forEach((date: string) => {
                    const isValidDate = isMatch(date, 'MM/dd/yyyy') || isMatch(date, 'MM-dd-yyyy');
                    if (!isValidDate) {
                        hasInvalidDates = true;
                    }
                });
                
                if (hasInvalidDates) {
                    errors.push({
                        row: rowIndex,
                        field: mapping.participantField,
                        message: 'All enrollment dates must be in MM/dd/yyyy or MM-dd-yyyy format',
                        value,
                    });
                }
                
                // Validate date count matches program count
                if (mappedData.programName) {
                    const programNames = mappedData.programName.split('|').map((p: string) => p.trim()).filter(Boolean);
                    const enrollmentDates = dates;
                    
                    // Check if counts match (or if there's exactly 1 date for all programs)
                    if (enrollmentDates.length !== 1 && enrollmentDates.length !== programNames.length) {
                        errors.push({
                            row: rowIndex,
                            field: mapping.participantField,
                            message: `Number of enrollment dates (${enrollmentDates.length}) must be 1 or match number of programs (${programNames.length})`,
                            value,
                        });
                    }
                }
            }

            mappedData[mapping.participantField] = value;
        });

        // Check if programName exists without enrollmentDate or vice versa
        /* if (mappedData.programName && !mappedData.enrollmentDate) {
            errors.push({
                row: rowIndex,
                field: 'enrollmentDate',
                message: 'Enrollment Date is required when Program Name is provided',
                value: '',
            });
        } */
        if (mappedData.enrollmentDate && !mappedData.programName) {
            errors.push({
                row: rowIndex,
                field: 'programName',
                message: 'Program Name is required when Enrollment Date is provided',
                value: '',
            });
        }

        // Validate household member required fields (only if household member has ANY data)
        Object.keys(householdMemberData).forEach(hhNumberStr => {
            const hhNumber = parseInt(hhNumberStr);
            const hhFields = householdMemberData[hhNumber];
            
            // If this household member has ANY field filled, check required fields
            if (hhFields.length > 0) {
                const requiredHHFields = optionalDateOfBirthEnabled
                    ? ['firstName', 'lastName']
                    : ['firstName', 'lastName', 'dateOfBirth'];//, 'relationshipId'
                
                requiredHHFields.forEach(requiredField => {
                    const fieldKey = `hh${hhNumber}_${requiredField}`;
                    const value = mappedData[fieldKey];
                    
                    // Check if this required field is missing or empty
                    if (!value || (typeof value === 'string' && value.trim() === '')) {
                        errors.push({
                            row: rowIndex,
                            field: fieldKey,
                            message: `HH${hhNumber} - ${formatFieldLabel(requiredField)} is required when household member data is provided`,
                            value: value || '',
                        });
                    }
                });
            }
        });

        return {
            rowIndex,
            data: mappedData,
            errors,
            isValid: errors.length === 0,
        };
    };

    const validateData = () => {
        setValidating(true);
        setTimeout(() => {
            const validated = csvData.map((row, index) => validateRow(row, index));
            setValidatedRows(validated);
            setValidating(false);
        }, 100);
    };

    // // Dataset-level (not row-level) check: a household key that appears on only one
    // // row likely indicates a typo. Surfaced as a non-blocking warning in the UI.
    // const findSingletonHouseholdKeys = (rows: ValidatedRow[]): Set<string> => {
    //     const keyCounts: Record<string, number> = {};

    //     rows.forEach(row => {
    //         const key = row.data.householdKey;
    //         if (key && key.trim() !== '') {
    //             keyCounts[key] = (keyCounts[key] || 0) + 1;
    //         }
    //     });

    //     // Return keys that appear only once
    //     return new Set(
    //         Object.entries(keyCounts)
    //             .filter(([_, count]) => count === 1)
    //             .map(([key]) => key)
    //     );
    // };

    // const singletonHouseholdKeys = findSingletonHouseholdKeys(validatedRows);

    const getValidCount = () => validatedRows.filter((r) => r.isValid).length;
    const getInvalidCount = () => validatedRows.filter((r) => !r.isValid).length;

    const getDisplayedRows2 = () => {
        const filtered = showInvalidOnly
            ? validatedRows.filter((r) => !r.isValid)
            : validatedRows;

        const start = (currentPage - 1) * ROWS_PER_PAGE;
        const end = start + ROWS_PER_PAGE;
        return filtered.slice(start, end);
    };

    const getDisplayedRows = () => {
        const filtered = validatedRows.filter((r) =>
            showInvalidOnly ? !r.isValid : r.isValid
        );

        const start = (currentPage - 1) * ROWS_PER_PAGE;
        const end = start + ROWS_PER_PAGE;

        return filtered.slice(start, end);
    };

    const getTotalPages = () => {
        const filtered = validatedRows.filter((r) =>
            showInvalidOnly ? !r.isValid : r.isValid
        );
        return Math.ceil(filtered.length / ROWS_PER_PAGE);
    };

    const getTotalPages2 = () => {
        const filtered = showInvalidOnly
            ? validatedRows.filter((r) => !r.isValid)
            : validatedRows;
        return Math.ceil(filtered.length / ROWS_PER_PAGE);
    };

    const formatFieldLabel = (field: string): string => {
        // Check if this is an additional info field
        const additionalInfoMatch = field.match(/^additionalInfo_(.+)$/);
        if (additionalInfoMatch) {
            const fieldId = additionalInfoMatch[1];
            const additionalField = referenceData.clientform.find(f => f.id === fieldId);
            if (additionalField) {
                return `Additional Info - ${additionalField.label}`;
            }
        }
        
        return field
            .replace(/([A-Z])/g, ' $1')
            .replace(/_/g, ' ')
            .replace(/^hh(\d+)/i, 'HH$1')
            .replace(/Id$/i, '')
            .replace(/Ids$/i, 's')
            .trim()
            .replace(/^\w/, (c) => c.toUpperCase());
    };

    const handleEdit = (rowIndex: number) => {
        const row = validatedRows[rowIndex];
        setEditingRow(rowIndex);
        setEditedData({ ...row.data });
        
        // Store current scroll position
        const scrollPosition = window.scrollY;
        
        // Restore scroll position after state update
        setTimeout(() => {
            removeStickyColumns();
            window.scrollTo(0, scrollPosition);
        }, 0);
    };

    const handleSaveEdit = async () => {
        if (editingRow !== null) {
            const updatedCsvData = [...csvData];
            columnMappings.forEach((mapping) => {
                if (editedData[mapping.participantField] !== undefined) {
                    updatedCsvData[editingRow][mapping.csvColumn] =
                        editedData[mapping.participantField];
                }
            });
            
            // Save to session
            if (sessionId) {
                try {
                    await updateSession(sessionId, { csvData: updatedCsvData });
                } catch (error) {
                    console.error('Error saving edit to session:', error);
                }
            }

            const validated = updatedCsvData.map((row, index) =>
                validateRow(row, index)
            );
            setValidatedRows(validated);
            setEditingRow(null);
            setEditedData({});
            
            toast.success('Row updated');
            setTimeout(() => {
                applyStickyColumns();
            }, 500);
        }
    };

    const handleCancelEdit = () => {
        setEditingRow(null);
        setEditedData({});
        setTimeout(() => {
            applyStickyColumns();
        }, 500);
    };

    const handleDownloadErrors = () => {
        const invalidRows = validatedRows.filter((r) => !r.isValid);
        const csvContent = [
            ['Row', 'Field', 'Error', 'Value'].join(','),
            ...invalidRows.flatMap((row) =>
                row.errors.map((error) =>
                    [
                        row.rowIndex + 1,
                        formatFieldLabel(error.field),
                        error.message,
                        `"${error.value || ''}"`,
                    ].join(',')
                )
            ),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'import_errors.csv';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Error report downloaded');
    };

    const handleNext = () => {
        //if (getInvalidCount() > 0) {
        if (getValidCount() === 0) {
            toast.error(
                'Please fix all validation errors before proceeding'
            );
            return;
        }
        onNext(validatedRows);
    };

    const handleTabChange = (showInvalid: boolean) => {
        setShowInvalidOnly(showInvalid);
        setCurrentPage(1);
        setTimeout(() => {
            applyStickyColumns();
        }, 500);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    if (validating) {
        
        return (
            <div className="import-step">
                <h2 className="section-header">Validation Preview</h2>
                <div className="card margin-vertical-4">
                    <div className="card-body text-center">
                        <div className="margin-vertical-5">
                            <i className="ri-loader-4-line rotating" style={{ fontSize: '48px', color: '#6b21a8' }} />
                            <p className="margin-top-3">Validating {csvData.length} rows...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="wizard-content">
            <div className="row justify-content-between margin-top-5 margin-bottom-5 step-button sticky-col" style={{ 
                                                                    top: '65px', 
                                                                    zIndex: 999
                                                                }}>
                <div className="column-auto">
                    <Button id="back-button" text="Back" className="secondary" onClick={onBack} />
                </div>
                <div className="column-auto">
                    <Button
                        id="next-button"
                        text="Begin Import"
                        icon={<i className="ri-arrow-right-line" />}
                        iconLocation="end"
                        onClick={handleNext}
                        disabled={getValidCount() === 0}
                    />
                </div>
            </div>

            <div className="import-step">
                <h2 className="section-header">Validation Preview</h2>

                <div className="card margin-vertical-4">
                    <div className="card-body">
                        <div className="row align-items-center hide">
                            <div className="column-auto">
                                <i
                                    className="ri-checkbox-circle-line"
                                    style={{ fontSize: '24px', color: '#16a34a' }}
                                />
                            </div>
                            <div className="column-auto">
                                <strong style={{ color: '#16a34a' }}>
                                    {getValidCount()} valid rows
                                </strong>
                            </div>
                            <div className="column-auto">
                                <i
                                    className="ri-error-warning-line"
                                    style={{ fontSize: '24px', color: '#dc2626' }}
                                />
                            </div>
                            <div className="column-auto">
                                <strong style={{ color: '#dc2626' }}>
                                    {getInvalidCount()} invalid rows
                                </strong>
                            </div>
                        </div>
                        <p className="margin-top-3">
                            Review the valid and invalid rows. All field values are validated against system reference data. Click "Fix" on any invalid field to edit it with dropdown options.
                        </p>
                    </div>
                </div>

                {/* singletonHouseholdKeys.size > 0 && (
                    <div className="card margin-vertical-4" style={{ backgroundColor: '#fef3c7', borderColor: '#f59e0b' }}>
                        <div className="card-body">
                            <div className="row align-items-start">
                                <div className="column-auto">
                                    <i className="ri-information-line" style={{ fontSize: '24px', color: '#d97706' }} />
                                </div>
                                <div className="column">
                                    <strong style={{ color: '#92400e' }}>
                                        {singletonHouseholdKeys.size} household key{singletonHouseholdKeys.size > 1 ? 's' : ''} appear{singletonHouseholdKeys.size > 1 ? '' : 's'} on only one row
                                    </strong>
                                    <p className="margin-top-2" style={{ fontSize: '0.875rem', color: '#92400e' }}>
                                        This is only a warning and does not block the import. A household key
                                        used by a single row may be a typo — verify you didn't mean to group
                                        more rows under the same key.
                                    </p>
                                    <ul className="margin-top-2" style={{ fontSize: '0.875rem', color: '#92400e' }}>
                                        {Array.from(singletonHouseholdKeys).map((key) => (
                                            <li key={key}>
                                                Household key "<strong>{key}</strong>" appears on only 1 row. Did you mean to group more rows under this key?
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                )*/}

                {getInvalidCount() > 0 && (
                    <div className="card margin-vertical-4" style={{ backgroundColor: '#fef3c7', borderColor: '#f59e0b', display: 'none' }}>
                        <div className="card-body">
                            <div className="row align-items-start">
                                <div className="column-auto">
                                    <i className="ri-information-line" style={{ fontSize: '24px', color: '#d97706' }} />
                                </div>
                                <div className="column">
                                    <strong style={{ color: '#92400e' }}>Choose a valid option in the following fields:</strong>
                                    <div className="row margin-top-2" style={{ fontSize: '0.875rem' }}>
                                        <div className="column-3">
                                            <strong>Genders:</strong>
                                            <div className="margin-top-1">
                                                {referenceData.genderOptions.map(g => g.label).join(', ')}
                                            </div>
                                        </div>
                                        <div className="column-3">
                                            <strong>Ethnicities:</strong>
                                            <div className="margin-top-1">
                                                {referenceData.ethnicityOptions.map(e => e.label).join(', ')}
                                            </div>
                                        </div>
                                        <div className="column-3">
                                            <strong>Races:</strong>
                                            <div className="margin-top-1">
                                                {referenceData.raceOptions.map(r => r.label).join(', ')}
                                            </div>
                                        </div>
                                        <div className="column-3">
                                            <strong>Relationships:</strong>
                                            <div className="margin-top-1">
                                                {referenceData.relationships.map(r => r.name).join(', ')}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row margin-top-2" style={{ fontSize: '0.875rem' }}>
                                        <div className="column-12">
                                            <strong>Programs:</strong>
                                            <div className="margin-top-1">
                                                {referenceData.programs.map(p => p.name).join(', ')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="row align-items-center margin-vertical-4">
                    <div className="column-auto">
                        <div className="tabs">
                            <button
                                className={`tab ${!showInvalidOnly ? 'active' : ''}`}
                                onClick={() => handleTabChange(false)}
                                style={{ color: '#16a34a', alignItems: 'center', display: 'flex' }}
                            >
                                <i
                                    className="ri-checkbox-circle-line"
                                    style={{ fontSize: '24px', color: '#16a34a' }}
                                />&nbsp;

                                Valid Rows ({getValidCount()})
                            </button>
                            <button
                                className={`tab ${showInvalidOnly ? 'active' : ''}`}
                                onClick={() => handleTabChange(true)}
                                style={{ color: '#dc2626', alignItems: 'center', display: 'flex' }}
                            >
                                <i
                                    className="ri-error-warning-line"
                                    style={{ fontSize: '24px', color: '#dc2626' }}
                                />&nbsp;

                                Invalid Rows ({getInvalidCount()})
                            </button>
                        </div>
                    </div>
                    {getInvalidCount() > 0 && (
                        <div className="column-auto">
                            <Button
                                id="download-errors"
                                text="Download Errors (CSV)"
                                className="secondary small"
                                icon={<i className="ri-download-line" />}
                                onClick={handleDownloadErrors}
                            />
                        </div>
                    )}
                </div>
                <div className="row justify-content-between padding-vertical-4 sticky-col" style={{ 
                                                                    top: '103px', 
                                                                    zIndex: 999
                                                                }}>
                    <div className="column-auto">
                        <div className="row justify-content-between">
                            <div className="column-auto">
                                <IconButton id="scroll-left"
                                    onClick={handleScrollLeft}
                                    className="small flat"
                                    icon={<i className="ri-arrow-left-s-line"></i>} />
                            </div>
                            <div className="column-auto">
                                <IconButton id="scroll-right"
                                    onClick={handleScrollRight}
                                    className="small flat"
                                    icon={<i className="ri-arrow-right-s-line"></i>} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="table-wrapper validate-table-wrapper">        
                    <div className="table-container">
                        <table className="table validation-table">
                            <thead>
                                <tr>
                                    <th>Actions</th>
                                    {columnMappings.slice(0, 200).map((mapping) => (
                                        <th key={mapping.participantField}>
                                            {formatFieldLabel(mapping.participantField)}
                                        </th>
                                    ))}
                                    {columnMappings.length > 200 && (
                                        <th>... +{columnMappings.length - 200} more</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {getDisplayedRows().map((row) => (
                                    <React.Fragment key={row.rowIndex}>
                                        <tr className={`${!row.isValid ? 'row-invalid' : ''} ${editingRow === row.rowIndex ? 'editing-row' : ''}`}>
                                            <td className="sticky-col sticky-first">
                                                {editingRow === row.rowIndex ? (
                                                    <div className="row">
                                                        <div className="column-auto">
                                                            <Button
                                                                id={`save-${row.rowIndex}`}
                                                                text="Save"
                                                                className="small"
                                                                onClick={handleSaveEdit}
                                                            />
                                                        </div>
                                                        <div className="column-auto">
                                                            <Button
                                                                id={`cancel-${row.rowIndex}`}
                                                                text="Cancel"
                                                                className="secondary small"
                                                                onClick={handleCancelEdit}
                                                            />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <Button
                                                        id={`edit-${row.rowIndex}`}
                                                        text="Edit"
                                                        className="secondary small"
                                                        onClick={() => handleEdit(row.rowIndex)}
                                                    />
                                                )}
                                            </td>
                                            {columnMappings.slice(0, 200).map((mapping, colIndex) => {
                                                const hasError = row.errors.some(
                                                    (e) => e.field === mapping.participantField
                                                );
                                                const value = row.data[mapping.participantField];

                                                if (editingRow === row.rowIndex) {
                                                    // Determine field type and render appropriate input
                                                    const fieldName = mapping.participantField;
                                                    const isGenderField = fieldName === 'genderId' || fieldName.includes('_genderId');
                                                    const isEthnicityField = fieldName === 'ethnicityId' || fieldName.includes('_ethnicityId');
                                                    const isRaceField = fieldName === 'raceIds' || fieldName.includes('_raceIds');
                                                    const isRelationshipField = fieldName === 'relationshipId' || fieldName.includes('_relationshipId');
                                                    const isProgramField = fieldName === 'programName';
                                                    
                                                    // Check if this is an additional info field
                                                    const isAdditionalInfoField = fieldName.includes('additionalInfo_');
                                                    let additionalInfoField = null;
                                                    if (isAdditionalInfoField) {
                                                        const fieldId = fieldName.replace('additionalInfo_', '');
                                                        additionalInfoField = referenceData.clientform.find(f => f.id === fieldId);
                                                    }
                                                    
                                                    const fieldError = row.errors.find(e => e.field === fieldName);
                                                    const hasFieldError = !!fieldError;
                                                    //console.log("colIndex 1: ", colIndex)
                                                    if(isGenderField){
                                                        console.log(editedData[mapping.participantField]);
                                                    }
                                                    
                                                    return (
                                                        <td 
                                                            key={mapping.participantField}
                                                            className={`
                                                                ${hasFieldError ? 'editing-cell-error' : 'editing-cell'}
                                                            `}
                                                            title={hasFieldError ? `Error: ${fieldError.message}` : ''}
                                                        >
                                                            {hasFieldError && (
                                                                <div style={{ 
                                                                    fontSize: '0.75rem', 
                                                                    color: '#dc2626', 
                                                                    marginBottom: '4px',
                                                                    fontWeight: 500
                                                                }}>
                                                                    <i className="ri-error-warning-line margin-right-1" />
                                                                    {fieldError.message}
                                                                </div>
                                                            )}
                                                            {isGenderField ? (() => {
                                                                const rawValue = editedData[mapping.participantField] || '';
                                                                const resolvedValue = findOptionValueByName(rawValue, referenceData.genderOptions);
                                                                const resolvedGender = referenceData.genderOptions.find(g => g.value === resolvedValue);
                                                                const selectValue = resolvedGender?.label ?? '';

                                                                return (
                                                                    <div>
                                                                    <select
                                                                        value={selectValue}
                                                                        onChange={(e) =>
                                                                            setEditedData({
                                                                                ...editedData,
                                                                                [mapping.participantField]: e.target.value, // store canonical name
                                                                            })
                                                                        }
                                                                        className={`form-control ${hasFieldError ? 'form-control-error' : ''}`}
                                                                        style={{ backgroundColor: hasFieldError ? '#fef2f2' : 'white' }}
                                                                    >
                                                                        <option value="">Select Gender</option>
                                                                        {referenceData.genderOptions.map(g => (
                                                                            <option key={g.value} value={g.label}>{g.label}</option>
                                                                        ))}
                                                                    </select>
                                                                    <div style={{ 
                                                                            fontSize: '0.75rem', 
                                                                            color: '#6b7280', 
                                                                            marginTop: '4px',
                                                                            fontStyle: 'italic'
                                                                        }}>
                                                                            Original: {(<span>{row.data[mapping.participantField] || ''}</span>)}
                                                                        </div>
                                                                    </div>        
                                                                );
                                                                })() : isEthnicityField ? (() => {
                                                                const rawValue = editedData[mapping.participantField] || '';
                                                                const resolvedValue = findOptionValueByName(rawValue, referenceData.ethnicityOptions, { allowSubstring: true });
                                                                const resolvedEthnicity = referenceData.ethnicityOptions.find(g => g.value === resolvedValue);
                                                                const selectValue = resolvedEthnicity?.label ?? '';

                                                                return (
                                                                    <div>
                                                                    <select
                                                                        value={selectValue}
                                                                        onChange={(e) =>
                                                                            setEditedData({
                                                                                ...editedData,
                                                                                [mapping.participantField]: e.target.value, // store canonical name
                                                                            })
                                                                        }
                                                                        className={`form-control ${hasFieldError ? 'form-control-error' : ''}`}
                                                                        style={{ backgroundColor: hasFieldError ? '#fef2f2' : 'white' }}
                                                                    >
                                                                        <option value="">Select Ethnicity</option>
                                                                        {referenceData.ethnicityOptions.map(g => (
                                                                            <option key={g.value} value={g.label}>{g.label}</option>
                                                                        ))}
                                                                    </select>
                                                                    <div style={{ 
                                                                            fontSize: '0.75rem', 
                                                                            color: '#6b7280', 
                                                                            marginTop: '4px',
                                                                            fontStyle: 'italic'
                                                                        }}>
                                                                            Original: {(<span>{row.data[mapping.participantField] || ''}</span>)}
                                                                        </div>
                                                                    </div>
                                                                );
                                                                })() : isRaceField ? (() => {
                                                                const rawValue = editedData[mapping.participantField] || '';
                                                                // Resolve pipe-delimited race values to their canonical names for multi-select
                                                                const selectedRaceNames = rawValue
                                                                    .split('|')
                                                                    .map((part: string) => {
                                                                        const resolvedValue = findOptionValueByName(part.trim(), referenceData.raceOptions, { allowSubstring: true });
                                                                        const race = referenceData.raceOptions.find(g => g.value === resolvedValue);
                                                                        return race?.label ?? '';
                                                                    })
                                                                    .filter(Boolean);

                                                                return (
                                                                    <div>
                                                                    <select
                                                                        multiple
                                                                        value={selectedRaceNames}
                                                                        onChange={(e) => {
                                                                            const selectedValues = Array.from(
                                                                                e.target.selectedOptions
                                                                            ).map(option => option.value);

                                                                            setEditedData({
                                                                                ...editedData,
                                                                                [mapping.participantField]: selectedValues.join('|'), // store as pipe-separated string
                                                                            });
                                                                        }}
                                                                        className={`form-control ${hasFieldError ? 'form-control-error' : ''}`}
                                                                        style={{
                                                                            backgroundColor: hasFieldError ? '#fef2f2' : 'white',
                                                                            height: '120px',
                                                                        }}
                                                                    >
                                                                        {referenceData.raceOptions.map(g => (
                                                                            <option key={g.value} value={g.label}>{g.label}</option>
                                                                        ))}
                                                                    </select>
                                                                    <div style={{ 
                                                                            fontSize: '0.75rem', 
                                                                            color: '#6b7280', 
                                                                            marginTop: '4px',
                                                                            fontStyle: 'italic'
                                                                        }}>
                                                                            Original: {(<span>{row.data[mapping.participantField] || ''}</span>)}
                                                                        </div>
                                                                    </div> 
                                                                );
                                                                })() : isRelationshipField ? (() => {
                                                                    const rawValue = editedData[mapping.participantField] || '';
                                                                    const resolvedId = rawValue.trim() !== ''
                                                                        ? findIdByName(rawValue, referenceData.relationships)
                                                                        : undefined;
                                                                    const resolvedRelationship = referenceData.relationships.find(g => g.id === resolvedId);
                                                                    const selectValue = resolvedRelationship?.name ?? '';

                                                                    return (
                                                                        <div>
                                                                        <select
                                                                            value={selectValue}
                                                                            onChange={(e) =>
                                                                                setEditedData({
                                                                                    ...editedData,
                                                                                    // Store empty string when user picks the blank option
                                                                                    [mapping.participantField]: e.target.value,
                                                                                })
                                                                            }
                                                                            onFocus={(e) =>
                                                                                setEditedData({
                                                                                    ...editedData,
                                                                                    // Store empty string when user picks the blank option
                                                                                    [mapping.participantField]: e.target.value,
                                                                                })
                                                                            }
                                                                            className={`form-control ${hasFieldError ? 'form-control-error' : ''}`}
                                                                            style={{ backgroundColor: hasFieldError ? '#fef2f2' : 'white' }}
                                                                        >
                                                                            {/* Empty option lets user clear/leave relationship blank */}
                                                                            <option value="">— None / Not provided —</option>
                                                                            {referenceData.relationships.map(g => (
                                                                                <option key={g.id} value={g.name}>{g.name}</option>
                                                                            ))}
                                                                        </select>
                                                                        <div style={{ 
                                                                            fontSize: '0.75rem', 
                                                                            color: '#6b7280', 
                                                                            marginTop: '4px',
                                                                            fontStyle: 'italic'
                                                                        }}>
                                                                            Original: {(<span>{row.data[mapping.participantField] || ''}</span>)}
                                                                        </div>
                                                                    </div> 
                                                                    );
                                                                })() : isProgramField ? (
                                                                    <div>
                                                                        <select
                                                                        multiple
                                                                        value={
                                                                            (editedData[mapping.participantField] || '')
                                                                            .split('|')
                                                                            .filter(Boolean)
                                                                        }
                                                                        onChange={(e) => {
                                                                            const selectedValues = Array.from(
                                                                            e.target.selectedOptions
                                                                            ).map(option => option.value);

                                                                            setEditedData({
                                                                            ...editedData,
                                                                            [mapping.participantField]:
                                                                                selectedValues.join('|'), // store as pipe-separated string
                                                                            });
                                                                        }}
                                                                        className={`form-control ${
                                                                            hasFieldError ? 'form-control-error' : ''
                                                                        }`}
                                                                        style={{
                                                                            backgroundColor: hasFieldError
                                                                            ? '#fef2f2'
                                                                            : 'white',
                                                                            height: '120px', // makes multi-select usable
                                                                        }}
                                                                        >
                                                                        {referenceData.programs.map(p => (
                                                                            <option key={p.id} value={p.name}>
                                                                            {p.name}
                                                                            </option>
                                                                        ))}
                                                                        </select>

                                                                        {/* Original display */}
                                                                        <div
                                                                        style={{
                                                                            fontSize: '0.75rem',
                                                                            color: '#6b7280',
                                                                            marginTop: '4px',
                                                                            fontStyle: 'italic'
                                                                        }}
                                                                        >
                                                                        Original: {(<span>{row.data[mapping.participantField] || ''}</span>)}
                                                                        </div>
                                                                    </div>
                                                                    )  : isAdditionalInfoField && additionalInfoField ? (
                                                                    <div>
                                                                        {additionalInfoField.fieldTypeId === 3 || additionalInfoField.fieldTypeId === 6 ? (
                                                                            // Radio or Select
                                                                            <select
                                                                                value={editedData[mapping.participantField] || ''}
                                                                                onChange={(e) =>
                                                                                    setEditedData({
                                                                                        ...editedData,
                                                                                        [mapping.participantField]: e.target.value,
                                                                                    })
                                                                                }
                                                                                className={`form-control ${hasFieldError ? 'form-control-error' : ''}`}
                                                                                style={{ backgroundColor: hasFieldError ? '#fef2f2' : 'white' }}
                                                                            >
                                                                                <option value="">Select...</option>
                                                                                {additionalInfoField.options?.map((opt: any) => (
                                                                                    <option key={opt.value} value={opt.label}>{opt.label}</option>
                                                                                ))}
                                                                            </select>
                                                                        ) : additionalInfoField.fieldTypeId === 5 ? (
                                                                            // Checkbox Group - use multi-select
                                                                            <select
                                                                                multiple
                                                                                value={
                                                                                    additionalInfoField.options
                                                                                        ?.filter((opt: any) => {
                                                                                            const csvValues = (editedData[mapping.participantField] || '')
                                                                                                .split(',')
                                                                                                .map((v: string) => v.trim().toLowerCase())
                                                                                                .filter(Boolean);

                                                                                            return csvValues.includes(
                                                                                                opt.label.toLowerCase()
                                                                                            );
                                                                                        })
                                                                                        .map((opt: any) => opt.label) || []
                                                                                }
                                                                                onChange={(e) => {
                                                                                    const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
                                                                                    setEditedData({
                                                                                        ...editedData,
                                                                                        [mapping.participantField]: selected.join(', '),
                                                                                    });
                                                                                }}
                                                                                className={`form-control ${hasFieldError ? 'form-control-error' : ''}`}
                                                                                style={{ 
                                                                                    backgroundColor: hasFieldError ? '#fef2f2' : 'white',
                                                                                    minHeight: '100px'
                                                                                }}
                                                                            >
                                                                                {additionalInfoField.options?.map((opt: any) => (
                                                                                    <option key={opt.value} value={opt.label}>{opt.label}</option>
                                                                                ))}
                                                                            </select>
                                                                        ) : additionalInfoField.fieldTypeId === 4 ? (
                                                                            // Single Checkbox
                                                                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={
                                                                                        ['true', 'yes', '1'].includes(
                                                                                            (editedData[mapping.participantField] || '')
                                                                                                .toString()
                                                                                                .trim()
                                                                                                .toLowerCase()
                                                                                        )
                                                                                    }
                                                                                    onChange={(e) =>
                                                                                        setEditedData({
                                                                                            ...editedData,
                                                                                            [mapping.participantField]: e.target.checked ? 'Yes' : 'No',
                                                                                        })
                                                                                    }
                                                                                />
                                                                                {additionalInfoField.label}
                                                                            </label>
                                                                        ) : additionalInfoField.fieldTypeId === 13 ? (
                                                                            // Date field
                                                                            <input
                                                                                type="date"
                                                                                value={editedData[mapping.participantField] || ''}
                                                                                onChange={(e) =>
                                                                                    setEditedData({
                                                                                        ...editedData,
                                                                                        [mapping.participantField]: e.target.value,
                                                                                    })
                                                                                }
                                                                                className={`form-control ${hasFieldError ? 'form-control-error' : ''}`}
                                                                                style={{ backgroundColor: hasFieldError ? '#fef2f2' : 'white' }}
                                                                            />
                                                                        ) : additionalInfoField.fieldTypeId === 2 ? (
                                                                            // Number field
                                                                            <input
                                                                                type="number"
                                                                                value={editedData[mapping.participantField] || ''}
                                                                                onChange={(e) =>
                                                                                    setEditedData({
                                                                                        ...editedData,
                                                                                        [mapping.participantField]: e.target.value,
                                                                                    })
                                                                                }
                                                                                className={`form-control ${hasFieldError ? 'form-control-error' : ''}`}
                                                                                style={{ backgroundColor: hasFieldError ? '#fef2f2' : 'white' }}
                                                                            />
                                                                        ) : additionalInfoField.fieldTypeId === 7 ? (
                                                                            // Multiline Text (textarea)
                                                                            <textarea
                                                                                value={editedData[mapping.participantField] || ''}
                                                                                onChange={(e) =>
                                                                                    setEditedData({
                                                                                        ...editedData,
                                                                                        [mapping.participantField]: e.target.value,
                                                                                    })
                                                                                }
                                                                                className={`form-control ${hasFieldError ? 'form-control-error' : ''}`}
                                                                                style={{ 
                                                                                    backgroundColor: hasFieldError ? '#fef2f2' : 'white',
                                                                                    minHeight: '80px'
                                                                                }}
                                                                                rows={3}
                                                                            />
                                                                        ) : (
                                                                            // Default text input for other types
                                                                            <input
                                                                                type="text"
                                                                                value={editedData[mapping.participantField] || ''}
                                                                                onChange={(e) =>
                                                                                    setEditedData({
                                                                                        ...editedData,
                                                                                        [mapping.participantField]: e.target.value,
                                                                                    })
                                                                                }
                                                                                className={`form-control ${hasFieldError ? 'form-control-error' : ''}`}
                                                                                style={{ backgroundColor: hasFieldError ? '#fef2f2' : 'white' }}
                                                                            />
                                                                        )}
                                                                        <div style={{ 
                                                                            fontSize: '0.75rem', 
                                                                            color: '#6b7280', 
                                                                            marginTop: '4px',
                                                                            fontStyle: 'italic'
                                                                        }}>
                                                                            Original: {row.data[mapping.participantField] || '(empty)'}
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                <div>
                                                                    <input
                                                                        type="text"
                                                                        value={editedData[mapping.participantField] || ''}
                                                                        onChange={(e) =>
                                                                            setEditedData({
                                                                                ...editedData,
                                                                                [mapping.participantField]: e.target.value,
                                                                            })
                                                                        }
                                                                        className={`form-control ${hasFieldError ? 'form-control-error' : ''}`}
                                                                        style={{ backgroundColor: hasFieldError ? '#fef2f2' : 'white' }}
                                                                        placeholder={hasFieldError ? 'Fix this field' : ''}
                                                                    />
                                                                    <div style={{ 
                                                                        fontSize: '0.75rem', 
                                                                        color: '#6b7280', 
                                                                        marginTop: '4px',
                                                                        fontStyle: 'italic'
                                                                    }}>
                                                                        Original: {row.data[mapping.participantField] || '(empty)'}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </td>
                                                    );
                                                }
                                                //console.log("colIndex 2: ", colIndex)
                                                return (
                                                    <td
                                                        key={mapping.participantField}
                                                        className={`
                                                                ${hasError ? 'text-danger' : ''}
                                                                ${colIndex === 0 ? 'sticky-col sticky-second' : ''}
                                                                ${colIndex === 2 ? 'sticky-col sticky-last' : ''}
                                                        `}
                                                        title={hasError ? `Error: ${row.errors.find(e => e.field === mapping.participantField)?.message}` : ''}
                                                    >
                                                        {hasError && (
                                                            <i className="ri-error-warning-line margin-right-1" />
                                                        )}
                                                        {value || <span className="text-muted">—</span>}
                                                        {hasError && (
                                                            <button
                                                                onClick={() => handleEdit(row.rowIndex)}
                                                                style={{
                                                                    marginLeft: '8px',
                                                                    padding: '2px 8px',
                                                                    fontSize: '0.75rem',
                                                                    border: '1px solid #dc2626',
                                                                    borderRadius: '4px',
                                                                    backgroundColor: 'white',
                                                                    color: '#dc2626',
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                Fix
                                                            </button>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                            {columnMappings.length > 200 && (
                                                <td className="text-muted">...</td>
                                            )}
                                        </tr>
                                        {!row.isValid && row.errors.length > 0 && (
                                            <tr className="error-row">
                                                <td
                                                    colSpan={Math.min(columnMappings.length + 1, 200)}
                                                    className="error-cell"
                                                >
                                                    <div className="alert alert-danger">
                                                        <i className="ri-alert-line margin-right-2" />
                                                        {row.errors.map((error, idx) => (
                                                            <div key={idx}>
                                                                <strong>
                                                                    {formatFieldLabel(error.field)}:
                                                                </strong>{' '}
                                                                {error.message}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>                
                {getTotalPages() > 1 && (
                    <div className="row justify-content-center margin-top-4">
                        <div className="column-auto">
                            <div className="pagination">
                                <button
                                    className="pagination-btn"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    <i className="ri-arrow-left-s-line" />
                                </button> 
                                <span className="pagination-info">
                                    Page {currentPage} of {getTotalPages()}
                                </span> 
                                <button
                                    className="pagination-btn"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === getTotalPages()}
                                >
                                    <i className="ri-arrow-right-s-line" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className="row justify-content-between margin-top-5 step-button">
                <div className="column-auto">
                    <Button id="back-button" text="Back" className="secondary" onClick={onBack} />
                </div>
                <div className="column-auto">
                    <Button
                        id="next-button"
                        text="Begin Import"
                        icon={<i className="ri-arrow-right-line" />}
                        iconLocation="end"
                        onClick={handleNext}
                        disabled={getValidCount() === 0}
                    />
                </div>
            </div>
        </div>
    );
};

export default ValidationPreviewStep;
